import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/redux';
import { loginSuccess, loginFailure } from '../../store/slices/authSlice';
import styled from '@emotion/styled';

const CallbackContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 50%, #FFB6B6 100%);
`;

const LoadingCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #FF6B6B;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const Message = styled.p`
  color: #333;
  font-size: 16px;
  margin: 0;
`;

const ErrorMessage = styled.div`
  background-color: #fee;
  color: #c53030;
  padding: 12px;
  border-radius: 6px;
  border-left: 4px solid #c53030;
  font-size: 14px;
  margin-top: 20px;
`;

const GoogleCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const token = searchParams.get('token');
    const error = searchParams.get('error');

    if (token) {
      // Verify and decode the token
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          
          dispatch(loginSuccess({
            token,
            user: {
              id: payload.id || payload.sub,
              email: payload.email,
              name: payload.name,
              role: payload.role || 'member',
              customerId: payload.customerId || null,
            },
          }));
          
          // Redirect to dashboard
          navigate('/', { replace: true });
        } else {
          throw new Error('Invalid token format');
        }
      } catch (err) {
        console.error('Token verification failed:', err);
        dispatch(loginFailure());
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      }
    } else if (error) {
      dispatch(loginFailure());
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } else {
      // No token or error, redirect to login
      navigate('/login', { replace: true });
    }
  }, [searchParams, navigate, dispatch]);

  const token = searchParams.get('token');
  const error = searchParams.get('error');

  return (
    <CallbackContainer>
      <LoadingCard>
        {!error && !token && (
          <>
            <LoadingSpinner />
            <Message>Processing Google Sign-In...</Message>
          </>
        )}
        {token && (
          <>
            <LoadingSpinner />
            <Message>Signing you in...</Message>
          </>
        )}
        {error && (
          <>
            <Message>Sign-in failed</Message>
            <ErrorMessage>
              {error === 'access_denied' 
                ? 'You cancelled the Google sign-in process.' 
                : 'There was an error signing you in with Google. Please try again.'}
            </ErrorMessage>
          </>
        )}
      </LoadingCard>
    </CallbackContainer>
  );
};

export default GoogleCallback;